export const procurementEndpoints = {
  createRequest: "/procurement/request",
  getRequestList: "/procurement/request/list",
  getRequestCounts: "/procurement/request/counts",
  getRequestById: (id: string) => `/procurement/request/${id}`,
  updateRequest: (id: string) => `/procurement/request/${id}`,
  beginRequest: (id: string) => `/procurement/request/${id}/begin`,
  /* SERV-2977: the travel lane's gate - the officer accepting what dispatch typed. */
  confirmTravelDetails: (id: string) => `/procurement/request/${id}/confirm-travel-details`,
  markItinerarySent: (id: string) => `/procurement/request/${id}/itinerary-sent`,
  completeRequest: (id: string) => `/procurement/request/${id}/complete`,
  /** SERV-2882: the quotation or receipt the completion guard checks for. */
  attachRequestQuotation: (id: string) => `/procurement/request/${id}/quotation`,
  cancelRequest: (id: string) => `/procurement/request/${id}/cancel`,
  confirmRequestDetails: (id: string) => `/procurement/request/${id}/confirm-details`,
  delayRequest: (id: string) => `/procurement/request/${id}/delay`,
  resumeRequest: (id: string) => `/procurement/request/${id}/resume`,
  /*
   * SERV-2508: the outbound phone-sale dispatcher-details check. Named *PhoneSaleDetails to stay
   * distinct from confirmRequestDetails above, which is SERV-2972's own mechanism for a different
   * lane, at a different path (/confirm-details vs /details-confirm).
   */
  confirmPhoneSaleDetails: (id: string) => `/procurement/request/${id}/details-confirm`,
  queryPhoneSaleDetails: (id: string) => `/procurement/request/${id}/details-query`,
  reopenPhoneSaleDetails: (id: string) => `/procurement/request/${id}/details-reopen`,
  getItemQuotationUrl: (id: string) => `/procurement/item/${id}/quotation-url`,
  getRequestQuotationUrl: (id: string, index: number) =>
    `/procurement/request/${id}/quotation-url?index=${index}`,
  addRequestItems: (id: string) => `/procurement/request/${id}/items`,
  assignRequest: (id: string) => `/procurement/request/${id}/assign`,
  bulkAssignRequests: "/procurement/request/assign",
  claimRequest: (id: string) => `/procurement/request/${id}/claim`,
  bulkClaimRequests: "/procurement/request/claim",
  getItemList: "/procurement/item/list",
  getItemById: (id: string) => `/procurement/item/${id}`,
  updateItem: (id: string) => `/procurement/item/${id}`,
  updateItemStatus: (id: string) => `/procurement/item/${id}/status`,
  /* SERV-2973: the rental hire, keyed by the ITEM the officer is acting on. */
  bookRentalAgreement: (id: string) => `/procurement/item/${id}/rental`,
  extendRentalAgreement: (id: string) => `/procurement/item/${id}/rental/extend`,
  startRentalHire: (id: string) => `/procurement/item/${id}/rental/on-hire`,
  endRentalHire: (id: string) => `/procurement/item/${id}/rental/off-rent`,
  settleRentalAgreement: (id: string) => `/procurement/item/${id}/rental/settle`,
  /** SERV-3100: mark the deposit returned, or undo it. */
  rentalDeposit: (id: string) => `/procurement/item/${id}/rental/deposit`,
  liveRentals: "/procurement/rentals/live",
  logItemPayment: (id: string) => `/procurement/item/${id}/payment`,
  updateItemReceipt: (id: string) => `/procurement/item/${id}/receipt`,
  /* SERV-2977: a booking's receipt - presign, read back, attach. */
  receiptUploadUrl: (id: string) => `/procurement/item/${id}/receipt-url`,
  itemReceipts: (id: string) => `/procurement/item/${id}/receipts`,
  /** SERV-3100: a rental's final-invoice files, presigned for viewing. */
  rentalInvoices: (id: string) => `/procurement/item/${id}/rental/invoices`,
  attachItemReceipts: (id: string) => `/procurement/item/${id}/receipts`,
  updateItemTracking: (id: string) => `/procurement/item/${id}/tracking`,
  /* SERV-2972: the two halves of "it arrived", and the officer's details check. */
  receiveItem: (id: string) => `/procurement/item/${id}/receive`,
  setItemEta: (id: string) => `/procurement/item/${id}/eta`,
  setItemPickup: (id: string) => `/procurement/item/${id}/pickup`,
  updateItemReturn: (id: string) => `/procurement/item/${id}/return`,
  buyItemReturnLabel: (id: string) => `/procurement/item/${id}/return/label`,
  resendItemReturnQrEmail: (id: string) => `/procurement/item/${id}/return/qr-email`,
  /* SERV-2888: return photo evidence. The item id is the only thing the browser names - the
   * bucket and the object key are both minted server side. */
  returnEvidenceUploadUrl: (id: string) => `/procurement/item/${id}/return/evidence-url`,
  getReturnEvidence: (id: string) => `/procurement/item/${id}/return/evidence`,
  attachReturnPhotos: (id: string) => `/procurement/item/${id}/return/photos`,
  getSupplierList: "/procurement/supplier/list",
  createSupplier: "/procurement/supplier",
  updateSupplier: (id: string) => `/procurement/supplier/${id}`,
  deleteSupplier: (id: string) => `/procurement/supplier/${id}`,
  inferSupplier: "/procurement/supplier/infer",
  getVendorAccountList: "/procurement/vendor-account/list",
  createVendorAccount: "/procurement/vendor-account",
  updateVendorAccount: (id: string) => `/procurement/vendor-account/${id}`,
  deleteVendorAccount: (id: string) => `/procurement/vendor-account/${id}`,
  linkVendorAccountCard: (id: string) => `/procurement/vendor-account/${id}/card`,
  unlinkVendorAccountCard: (id: string) => `/procurement/vendor-account/${id}/card`,
  facilitySearch: "/procurement/facility-search",
  quotationUploadUrl: "/procurement/quotation-upload-url",
  quotationExtract: "/procurement/quotation-extract",
  flightSearch: "/procurement/flight-search",
  returnPolicySearch: "/procurement/return-policy-search",
  verifyTracking: "/procurement/verify-tracking",
  createSupplierLocation: (supplierId: string) => `/procurement/supplier/${supplierId}/location`,
  getSupplierLocationList: (supplierId: string) =>
    `/procurement/supplier/${supplierId}/location/list`,
  getPaymentCardList: "/procurement/payment-card/list",
  createPaymentCard: "/procurement/payment-card",
  partSearch: "/procurement/part-search",
  equipmentCatalogue: "/procurement/equipment-catalogue",
  linkExtract: "/procurement/link-extract",
  expiryAlerts: "/procurement/alerts",
  /* SERV-3440: every dated procurement event in a window, plus the undated live lines. */
  calendar: "/procurement/calendar",
  /* SERV-2966 gap 4: money actually received against a settled return. */
  returnCredit: (itemId: string) => `/procurement/item/${itemId}/return-credit`,
  returnReconciliation: (itemId: string) => `/procurement/item/${itemId}/return-reconciliation`,
  /* SERV-2966 gap 6: the supplier record is now the authoritative copy of a vendor's terms. */
  supplierReturnPolicy: (supplierId: string) => `/procurement/supplier/${supplierId}/return-policy`,
  storageUnitList: "/procurement/storage-unit",
  storageUnit: (id: string) => `/procurement/storage-unit/${id}`,
  storageUnitCodes: (id: string) => `/procurement/storage-unit/${id}/codes`,
  /* SERV-2974: the unit's field-level edit history. Discloses no codes. */
  storageUnitHistory: (id: string) => `/procurement/storage-unit/${id}/history`,
  storageUnitAccessRequests: (id: string) => `/procurement/storage-unit/${id}/access-request`,
  /*
   * SERV-2974: grant and revoke. Revoke is a POST on a sub-path rather than a DELETE, matching the
   * server - the row is not deleted, only stamped with `revokedAt`, because who could enter and
   * until when is what the roster exists to record.
   */
  storageUnitAccess: (id: string) => `/procurement/storage-unit/${id}/access`,
  storageUnitAccessRevoke: (id: string, accessId: string) =>
    `/procurement/storage-unit/${id}/access/${accessId}/revoke`,
  createPricingRequest: "/procurement/pricing-request",
  getPricingRequestList: "/procurement/pricing-request/list",
  getPricingRequestById: (id: string) => `/procurement/pricing-request/${id}`,
  claimPricingRequest: (id: string) => `/procurement/pricing-request/${id}/claim`,
  requestPricingSpInfo: (id: string) => `/procurement/pricing-request/${id}/request-sp-info`,
  markPricingSpAnswered: (id: string) => `/procurement/pricing-request/${id}/sp-answered`,
  submitPricingQuote: (id: string) => `/procurement/pricing-request/${id}/submit-quote`,
  cancelPricingRequest: (id: string) => `/procurement/pricing-request/${id}/cancel`,
  /* SERV-2975: the confirm-details gate, sourcing options, and the send/decide/return loop. */
  confirmPricingDetails: (id: string) => `/procurement/pricing-request/${id}/confirm-details`,
  reopenPricingDetailsCheck: (id: string) =>
    `/procurement/pricing-request/${id}/reopen-details-check`,
  queryPricingDetails: (id: string) => `/procurement/pricing-request/${id}/query-details`,
  addPricingOption: (id: string) => `/procurement/pricing-request/${id}/option`,
  removePricingOption: (id: string, optionId: string) =>
    `/procurement/pricing-request/${id}/option/${optionId}`,
  getPricingOptionQuotationUrl: (id: string, optionId: string, index: number) =>
    `/procurement/pricing-request/${id}/option/${optionId}/quotation-url?index=${index}`,
  sendPricingOptions: (id: string) => `/procurement/pricing-request/${id}/send-options`,
  pullBackPricingOptions: (id: string) => `/procurement/pricing-request/${id}/pull-back-options`,
  recordPricingDecision: (id: string) => `/procurement/pricing-request/${id}/decision`,
  returnPricingAnswer: (id: string) => `/procurement/pricing-request/${id}/return-answer`,
  noPricingOptionFound: (id: string) => `/procurement/pricing-request/${id}/no-option-found`,
  getOfficerOptions: "/procurement/officer-options",
  /* SERV-3096: read-only lookups owned by the procurement module, so an officer never has to call
   * the work-order or rates modules (their permission codes are not part of the officer role). */
  workOrderOptions: "/procurement/work-order-options",
  workOrderContext: (workOrderId: string) => `/procurement/work-order/${workOrderId}/context`,
  userOptions: "/procurement/user-options",
  teamLeadOptions: "/procurement/team-lead-options",
  workOrderTeamLeads: (workOrderId: string) => `/procurement/work-order/${workOrderId}/team-leads`,
  // SERV-2508 (adopted by SERV-2972): the request's history, for the Activity tab.
  getRequestActivities: (id: string) => `/procurement/request/${id}/activity`,
  // SERV-3020: the kebab-menu "Add internal note".
  addRequestNote: (id: string) => `/procurement/request/${id}/note`,
  replyToDispatcher: (id: string) => `/procurement/request/${id}/reply`,
  resubmitToProcurement: (id: string) => `/procurement/request/${id}/resubmit`,
  // SERV-3353 3.D: the conversation's officer send-back, the creator's message, and who to @-tag.
  sendBackToDispatcher: (id: string) => `/procurement/request/${id}/send-back`,
  messageProcurement: (id: string) => `/procurement/request/${id}/message`,
  getMentionableUsers: (id: string) => `/procurement/request/${id}/mentionable`,
  // SERV-3500: a presigned PUT for one file attached to a conversation message.
  conversationAttachmentUrl: (id: string) =>
    `/procurement/request/${id}/conversation/attachment-url`,
  submitCorrections: (id: string) => `/procurement/request/${id}/corrections`,
} as const;
